import { z } from 'zod';
import { parseApiDateString } from '../globalFilters/globalFiltersUseCase';

/**
 * Extract a single value from from the values array
 * Used to convert the form's values array format to a single string value
 * @param {Array<string>} values
 * @returns {string}
 */
export const getSingleValueField = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return '';
  }
  return values[0] || '';
};

/**
 * Extract a multiple values from from the values array
 * Used to ensure the values array is properly formatted for the multiValue field.
 * @param {Array<string>} values
 * @returns {Array<string>}
 */
export const getMultipleValueField = (/** @type {Array<string>} */ values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }
  return values;
};

/**
 * Create an onChange handler for single value fields
 * Wraps a single value onChange handler to ensure the value is stored as an array.
 * @param {(values: Array<string>) => void} onChange
 * @returns {(newValue: string) => void}
 */
export const createSingleValueOnChange = (onChange) => (newValue) => {
  const valueToStore = typeof newValue === 'string' ? newValue : '';
  onChange([valueToStore]);
};

/**
 * Whether a conditional filter type stores multiple values in filterValues.values
 * @param {ConditionalFilter['type'] | undefined} type
 * @returns {boolean}
 */
export const isMultiValueType = (type) => type === 'select_multiple' || type === 'dateRange';

/**
 * Read start/end dates from a dateRange values array
 * @param {Array<string>} values
 * @returns {{ start: string, end: string }}
 */
export const getDateRangeValueField = (values) => {
  if (!Array.isArray(values)) {
    return { start: '', end: '' };
  }
  return {
    start: values[0] || '',
    end: values[1] || '',
  };
};

/**
 * Update one bound of a dateRange values array without overwriting the other
 * @param {Array<string>} values
 * @param {'start' | 'end'} bound
 * @param {string} newValue
 * @returns {Array<string>}
 */
export const updateDateRangeValueField = (values, bound, newValue) => {
  const current = getDateRangeValueField(values);
  const nextValue = typeof newValue === 'string' ? newValue : '';
  if (bound === 'start') {
    return [nextValue, current.end];
  }
  return [current.start, nextValue];
};

/**
 * Validation regex patterns
 */
const TIME_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

/**
 * Create dynamic Zod schema based on validation rules
 * Uses native Zod validations applied directly on the schema
 * @param {Record<string, ConditionalFilter>} conditionalFiltersById
 * @returns {z.ZodType}
 */
export const createValidationSchema = (conditionalFiltersById) => {
  const baseSchema = z.object({
    id: z.string().optional(),
    operator: z.string().optional(),
    values: z.array(z.string()).default([]),
    singleValue: z.boolean().default(true),
  })
    // Validate id is non-empty if provided
    .refine(
      (data) => {
        if (data.id !== undefined && data.id !== null) {
          return data.id.trim().length > 0;
        }
        return true;
      },
      {
        message: 'conditionalFilters.id.required',
        path: ['id'],
      },
    )
    // Validate operator is non-empty if id is provided
    .refine(
      (data) => {
        if (data.id && data.id.trim().length > 0) {
          return data.operator && data.operator.trim().length > 0;
        }
        return true;
      },
      {
        message: 'conditionalFilters.operator.required',
        path: ['operator'],
      },
    )
    // Validate operator is in the operator options
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const filter = conditionalFiltersById[data.id];
        if (filter && filter?.operatorOptions?.length) {
          const { operatorOptions } = filter;
          /** @type {Array<string>} */
          const options = operatorOptions;
          return options.includes(data.operator);
        }
        return true;
      },
      {
        message: 'conditionalFilters.operator.invalid',
        path: ['operator'],
      },
    )
    // Text validation: allow empty strings for all text filters (API handles it)
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = conditionalFiltersById[data.id]?.type;
        if (type !== 'text') return true;

        const value = getSingleValueField(data.values);

        // Allow empty strings for all text filters (API handles empty strings)
        return typeof value === 'string';
      },
      {
        message: 'conditionalFilters.text.required',
        path: ['values'],
      },
    )
    // Date validation: valid date string
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = conditionalFiltersById[data.id]?.type;
        if (type !== 'date') return true;

        const value = getSingleValueField(data.values);
        if (!value) return false;
        const date = new Date(value);
        return date instanceof Date && !Number.isNaN(date.getTime());
      },
      {
        message: 'conditionalFilters.date.required',
        path: ['values'],
      },
    )
    // Time validation: valid time format (HH:MM)
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = conditionalFiltersById[data.id]?.type;
        if (type !== 'time') return true;

        const value = getSingleValueField(data.values);

        return value && typeof value === 'string' && TIME_REGEX.test(value.trim());
      },
      {
        message: 'conditionalFilters.time.required',
        path: ['values'],
      },
    )
    // Select validation: non-empty string after trim
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = conditionalFiltersById[data.id]?.type;
        if (type !== 'select') return true;

        const value = getSingleValueField(data.values);

        return value && typeof value === 'string' && value.trim() !== '';
      },
      {
        message: 'conditionalFilters.select.required',
        path: ['values'],
      },
    )
    // Select multiple validation: non-empty array with non-empty strings
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = conditionalFiltersById[data.id]?.type;
        if (type !== 'select_multiple') return true;

        const value = getMultipleValueField(data.values);

        return Array.isArray(value)
          && value.length > 0
          && value.every((item) => typeof item === 'string' && item.trim() !== '');
      },
      {
        message: 'conditionalFilters.select_multiple.required',
        path: ['values'],
      },
    )
    // Date range validation: exactly two valid dates, start <= end
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = conditionalFiltersById[data.id]?.type;
        if (type !== 'dateRange') return true;

        const { start, end } = getDateRangeValueField(data.values);
        if (!start || !end) return false;

        const startDate = parseApiDateString(start);
        const endDate = parseApiDateString(end);

        return Boolean(startDate && endDate);
      },
      {
        message: 'conditionalFilters.dateRange.required',
        path: ['values'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.operator) return true;
        const type = conditionalFiltersById[data.id]?.type;
        if (type !== 'dateRange') return true;

        const { start, end } = getDateRangeValueField(data.values);
        const startDate = parseApiDateString(start);
        const endDate = parseApiDateString(end);

        if (!startDate || !endDate) return true;

        return startDate.getTime() <= endDate.getTime();
      },
      {
        message: 'conditionalFilters.dateRange.order',
        path: ['values'],
      },
    );

  return z.object({
    conditionalFilters: z.array(baseSchema).optional().default([]),
  });
};

/**
 * Find and return the non-null filter property
 * Iterates through all properties of the filter object to find the one that is not null
 * This function is maintainable - you can add new filter types without modifying it
 * @param {ApiManagerGlobalFiltersFilter} filter
 * @returns {ConditionalFilter | null}
 */
export const getNonNullFilterWithType = (filter) => {
  // Find the first non-null and non-undefined value
  if (!filter) return null;

  if (filter?.select) {
    return { ...filter.select, type: 'select' };
  }
  if (filter?.select_multiple) {
    return { ...filter.select_multiple, type: 'select_multiple' };
  }
  if (filter?.dateRange) {
    return { ...filter.dateRange, type: 'dateRange' };
  }
  if (filter?.date) {
    return { ...filter.date, type: 'date' };
  }
  if (filter?.time) {
    return { ...filter.time, type: 'time' };
  }
  if (filter?.text) {
    return { ...filter.text, type: 'text' };
  }

  return null;
};
